import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
// import OverviewView from './components/OverviewView';
import AdvancedFilterPanel, {
  FilterButton,
} from "../../components/common/AdvancedFilter";
import FilterAwarenessBar from "../../components/common/AdvancedFilter/FilterAwarenessBar";
import {
  getActivePeriodRange,
  getActiveFilterCount,
} from "../../components/common/AdvancedFilter/periodUtils";
import {
  GenericView,
  SearchInput,
  ActionButtons,
} from "../../components/common/table";
import Alert from "../../components/Alert/Alert";
import ConfirmationModal from "../../components/common/ConfirmationModal";
import api from "../../api";
import { useAuth } from "../../contexts/AuthProvider";
import * as XLSX from "xlsx";
import { usePersistedFilters } from "../../hooks/usePersistedFilters";

// Map frontend column keys to backend sort property names (Spring sort param)
const PROJECT_SORT_KEY_TO_BACKEND = {
  project: "projectCode",
  client: "companyName",
  projectDate: "projectDate",
  validUntil: "validUntil",
  partnerType: "partnerType",
  amount: "grandTotal",
  createdAt: "createdAt",
  assignedTo: "assignedTo",
  createdBy: "createdBy",
  yearLeft: "yearLeft",
  tagging: "tagging",
  projectName: "projectName",
  department: "departmentName",
};

// Serialize filter value (array or string) to API param: "all" or "id1,id2,..."
const toFilterParam = (v) => {
  if (Array.isArray(v)) {
    const clean = v.filter((x) => x != null && x !== "" && x !== "all");
    return clean.length === 0 ? "all" : clean.join(",");
  }
  return v == null || v === "" ? "all" : String(v);
};

/** Convert the original project total to IDR using its creation-time rate. */
const getProjectAmountInIdr = (item) => {
  const sourceAmount = Number(item.grandTotalDisplayCurrency ?? item.grandTotal ?? 0);
  if (!Number.isFinite(sourceAmount)) return 0;

  const currencyCode = String(item.displayCurrencyCode || "IDR").trim().toUpperCase();
  if (currencyCode === "IDR") return sourceAmount;

  const creationRate = Number(item.exchangeRateAtCreation);
  return Number.isFinite(creationRate) && creationRate > 0
    ? sourceAmount * creationRate
    : sourceAmount;
};

const getEmptyProjectFilters = () => ({
  period: "",
  startDate: "",
  endDate: "",
  projectStartDateFrom: "",
  projectStartDateTo: "",
  projectEndDateFrom: "",
  projectEndDateTo: "",
  createdDateFrom: "",
  createdDateTo: "",
  proposeDateFrom: "",
  proposeDateTo: "",
  assignedUser: [],
  createdBy: [],
  department: [],
  branchId: [],
  stage: "",
  sector: [],
  source: [],
  standards: [],
  service: [],
  taggingIds: [],
});

const Project = () => {
  const navigate = useNavigate();
  const { loadPersistedState, persistState } = usePersistedFilters("project");
  const [searchQuery, setSearchQuery] = useState("");

  const [filterData, setFilterData] = useState(getEmptyProjectFilters);
  const [appliedFilters, setAppliedFilters] = useState(getEmptyProjectFilters);

  const [taggingOptions, setTaggingOptions] = useState([]);
  const [branches, setBranches] = useState([]);

  // Tambahkan state untuk data project
  const [projects, setProjects] = useState([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0); // backend-computed sum (e.g. for the year)
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // State for filter options
  const [filterOptions, setFilterOptions] = useState({
    assignedUsers: [],
    createdByUsers: [],
    departments: [],
    standards: [],
    sectors: [],
    sources: [],
    services: [],
  });

  // ✅ PERFORMANCE: Track if filter options have been loaded (lazy-load guard)
  const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);

  // Use global auth context with capabilities
  const { user, can, capabilitiesLoading } = useAuth();

  // Helper function to check project-specific capabilities
  const canProject = (capabilityKey) => can(capabilityKey, "project");

  const projectBaseAmount = (project) =>
    parseFloat(project.amount) ||
    parseFloat(project.grandTotalBaseCurrency) ||
    parseFloat(project.grandTotal) ||
    0;

  // ✅ MULTI-CURRENCY: Helper function untuk mendapatkan currency symbol
  const getCurrencySymbol = (currencyCode) => {
    switch (currencyCode) {
      case "USD":
        return "$";
      case "EUR":
        return "€";
      case "JPY":
        return "¥";
      case "SGD":
        return "S$";
      case "IND":
        return "RUP";
      default:
        return "Rp";
    }
  };

  // ✅ MULTI-CURRENCY: Helper function untuk format amount dengan currency
  const formatAmount = (amount, currencyCode = "IDR") => {
    if (!amount || amount === 0) {
      return `${getCurrencySymbol(currencyCode)} 0`;
    }

    const symbol = getCurrencySymbol(currencyCode);
    const formattedAmount = amount.toLocaleString("id-ID");
    return `${symbol} ${formattedAmount}`;
  };

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000); // Auto hide after 5 seconds
  };

  // Cleanup modal backdrop when component unmounts or modal state changes
  useEffect(() => {
    return () => {
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [showDeleteModal]);

  // ✅ FETCH FILTER OPTIONS — lazy-loaded, all calls parallel, single user fetch
  const fetchFilterOptions = async () => {
    if (filterOptionsLoaded) return;
    try {
      const [
        usersRes,
        deptRes,
        standardsRes,
        taggingRes,
        userBranchesRes,
        sectorsRes,
        sourcesRes,
        servicesRes,
      ] = await Promise.all([
        api
          .get("/md/internal-users/option-internal-user")
          .catch(() => ({ data: [] })),
        api.get("/crm/departments").catch(() => ({ data: [] })),
        api.get("/md/standards").catch(() => ({ data: [] })),
        api
          .get("/md/tagging", { params: { moduleKey: "companies" } })
          .catch(() => ({ data: [] })),
        user?.id
          ? api
              .get(`/user-branches/user/${user.id}`)
              .catch(() => ({ data: { data: [] } }))
          : Promise.resolve({ data: { data: [] } }),
        api.get("/dashboard/sectors").catch(() => ({ data: { sectors: [] } })),
        api.get("/dashboard/sources").catch(() => ({ data: { sources: [] } })),
        api.get("/crm/services/options").catch(() => ({ data: [] })),
      ]);

      // ✅ Permission-based filtering: applied ONCE to the single user fetch
      let filteredUsers = usersRes.data || [];
      if (!canProject("view_global")) {
        if (canProject("view_own")) {
          if (user?.id) {
            const userId = String(user.id);
            filteredUsers = filteredUsers.filter(
              (u) => String(u.id) === userId,
            );
          }
        } else if (canProject("view_department")) {
          if (user?.departmentId) {
            const userDeptId = String(user.departmentId);
            filteredUsers = filteredUsers.filter((u) => {
              const uDeptId = u.departmentId || u.departementId;
              return uDeptId != null && String(uDeptId) === userDeptId;
            });
          } else {
            filteredUsers = [];
          }
        }
      }

      // ✅ Reuse the same filtered list for both dropdowns (was 2 identical API calls)
      const userOptions = filteredUsers.map((u) => ({
        value: u.id,
        label: u.fullName || `${u.firstName} ${u.lastName}`.trim(),
        departmentId: u.departmentId || u.departementId,
        status: u.status,
      }));

      // Departments — permission-filtered
      let deptData = deptRes.data || [];
      if (!canProject("view_global")) {
        if (user?.departmentId) {
          const userDeptId = String(user.departmentId);
          deptData = deptData.filter((dept) => {
            const deptId = String(dept.id || dept.value || "");
            return deptId === userDeptId;
          });
        } else {
          deptData = [];
        }
      }
      const departments = deptData.map((dept) => ({
        value: dept.id,
        label: dept.name,
      }));

      const standards = (standardsRes.data || []).map((s) => ({
        value: s.id,
        label: s.name,
      }));
      setTaggingOptions(taggingRes.data || []);
      const branchData =
        userBranchesRes.data?.data ?? userBranchesRes.data ?? [];
      setBranches(Array.isArray(branchData) ? branchData : []);
      const sectors = (sectorsRes.data?.sectors || []).map((s) => ({
        value: s.id,
        label: s.name,
      }));
      const sources = (sourcesRes.data?.sources || []).map((s) => ({
        value: s.id,
        label: s.name,
      }));
      const services = (servicesRes.data || []).map((s) => ({
        value: s.id ?? s.value,
        label: s.name ?? s.label,
      }));

      setFilterOptions((prev) => ({
        ...prev,
        assignedUsers: userOptions,
        createdByUsers: userOptions,
        departments,
        standards,
        sectors,
        sources,
        services,
      }));
      setFilterOptionsLoaded(true);
    } catch (error) {
      console.error("Error fetching filter options:", error);
      setFilterOptions((prev) => ({
        ...prev,
        assignedUsers: [],
        createdByUsers: [],
        departments: [],
        standards: [],
      }));
    }
  };

  // ✅ PERFORMANCE: Lazy-load filter options only when user opens the filter panel
  const handleOpenFilter = () => {
    fetchFilterOptions();
  };

  // ✅ SEARCH: Fetch data project dari API dengan search functionality (backend returns Page<T>)
  const fetchProjects = async (
    searchTerm = "",
    taggingFilter = [],
    pageNum = page,
    pageSize = size,
    sortConfigOverride = null,
    filterDataOverride = null,
  ) => {
    const effectiveFilterData = filterDataOverride ?? appliedFilters;
    setLoading(true);
    try {
      // Build query params
      const params = { page: pageNum, size: pageSize };

      // ✅ Server-side sort: backend expects sort=propertyName,direction (e.g. sort=companyName,asc)
      const effectiveSort = sortConfigOverride ?? sortConfig;
      if (
        effectiveSort?.key &&
        PROJECT_SORT_KEY_TO_BACKEND[effectiveSort.key]
      ) {
        const backendSortProperty =
          PROJECT_SORT_KEY_TO_BACKEND[effectiveSort.key];
        params.sort = `${backendSortProperty},${effectiveSort.direction}`;
      }

      // ✅ SEARCH: Add search query parameter
      if (searchTerm && searchTerm.trim() !== "") {
        params.search = searchTerm.trim();
      }

      // ✅ DATE FILTER: Add date filter parameters for backend
      if (effectiveFilterData.projectStartDateFrom) {
        params.projectStartDateFrom = effectiveFilterData.projectStartDateFrom;
      }
      if (effectiveFilterData.projectStartDateTo) {
        params.projectStartDateTo = effectiveFilterData.projectStartDateTo;
      }
      if (effectiveFilterData.projectEndDateFrom) {
        params.projectEndDateFrom = effectiveFilterData.projectEndDateFrom;
      }
      if (effectiveFilterData.projectEndDateTo) {
        params.projectEndDateTo = effectiveFilterData.projectEndDateTo;
      }
      // ✅ PERIOD FILTER: period preset overrides manual createdDate range
      const periodStart = effectiveFilterData.startDate;
      const periodEnd = effectiveFilterData.endDate;
      if (periodStart) {
        params.createdAtFrom = periodStart;
      } else if (effectiveFilterData.createdDateFrom) {
        params.createdAtFrom = effectiveFilterData.createdDateFrom;
      }
      if (periodEnd) {
        params.createdAtTo = periodEnd;
      } else if (effectiveFilterData.createdDateTo) {
        params.createdAtTo = effectiveFilterData.createdDateTo;
      }

      // ✅ MULTISELECT FILTERS: serialize arrays to comma-separated ids for backend
      const standardsParam = toFilterParam(effectiveFilterData.standards);
      const serviceParam = toFilterParam(effectiveFilterData.service);
      const branchParam = toFilterParam(effectiveFilterData.branchId);
      const sectorParam = toFilterParam(effectiveFilterData.sector);
      const sourceParam = toFilterParam(effectiveFilterData.source);
      const assignedUserParam = toFilterParam(effectiveFilterData.assignedUser);
      const departmentParam = toFilterParam(effectiveFilterData.department);
      const createdByParam = toFilterParam(effectiveFilterData.createdBy);

      // ✅ STANDARDS FILTER: Add standards filter parameter
      if (standardsParam !== "all") {
        params.standards = standardsParam;
      }

      // ✅ SERVICE FILTER: Add service filter parameter
      if (serviceParam !== "all") {
        params.service = serviceParam;
      }

      // ✅ BRANCH FILTER
      if (branchParam !== "all") {
        params.branchId = branchParam;
      }

      if (sectorParam !== "all") {
        params.sector = sectorParam;
      }

      if (sourceParam !== "all") {
        params.source = sourceParam;
      }

      // ✅ ASSIGNED USER FILTER: backend param name is "assignedTo" (filters by lead's assignedUser.id)
      if (assignedUserParam !== "all") {
        params.assignedTo = assignedUserParam;
      }

      // ✅ DEPARTMENT FILTER: backend param name is "department" (filters by lead's assignedUser departmentId)
      if (departmentParam !== "all") {
        params.department = departmentParam;
      }

      // ✅ CREATED BY FILTER: backend param name is "createdById" (filters by project cuser/createdBy)
      if (createdByParam !== "all") {
        params.createdById = createdByParam;
      }

      // ✅ AUDIT DATE FILTER: filter by per-standard propose_date
      if (effectiveFilterData.proposeDateFrom) {
        params.proposeDateFrom = effectiveFilterData.proposeDateFrom;
      }
      if (effectiveFilterData.proposeDateTo) {
        params.proposeDateTo = effectiveFilterData.proposeDateTo;
      }

      // ✅ TAGGING FILTER: send to backend as comma-separated IDs (backend handles server-side)
      if (taggingFilter && taggingFilter.length > 0) {
        params.tagging = taggingFilter.join(",");
      }

      // ✅ Permission-based filtering hierarchy:
      // 1. view_global → see all (no filter)
      // 2. view_department → filter by assigned user's department
      // 3. view_own → filter by user ID (created by OR assigned to)

      if (!canProject("view_global")) {
        if (canProject("view_department")) {
          // Filter by department (assigned user's department for projects)
          if (user?.departmentId) {
            params.viewDepartmentId = user.departmentId;
          }
        } else if (canProject("view_own")) {
          // Filter by user (created by OR assigned to)
          if (user?.id) {
            params.viewOwnUserId = user.id;
          }
        }
      }

      const res = await api.get("/v2/crm/projects", { params });
      const data = res.data;
      const rawList = Array.isArray(data) ? data : (data?.content ?? []);
      let projectsData = rawList;
      // Total amount: use backend-computed total (all filtered projects)
      setTotalAmount(data?.totalAmount != null ? Number(data.totalAmount) : 0);

      // All filtering is server-side — use backend totals directly
      const backendTotalElements =
        data && typeof data.totalElements === "number"
          ? data.totalElements
          : projectsData.length;
      const backendTotalPages =
        data?.totalPages ??
        Math.max(1, Math.ceil(backendTotalElements / pageSize));
      setTotalElements(backendTotalElements);
      setTotalPages(backendTotalPages);

      const mappedProjects = await Promise.all(projectsData.map(async (item) => {
        let currencyCode = String(item.displayCurrencyCode || "").trim().toUpperCase();
        if (!currencyCode && item.displayCurrencyId) {
          const currencyResponse = await api.get(`/crm/currencies/${item.displayCurrencyId}`).catch(() => null);
          currencyCode = String(currencyResponse?.data?.currencyCode || "").trim().toUpperCase();
        }
        currencyCode = currencyCode || "IDR";
        const amountItem = {
          ...item,
          displayCurrencyCode: currencyCode,
          ...(currencyCode === "IDR" ? { exchangeRateAtCreation: 1 } : {})
        };
        return {
        ...amountItem,
        project: item.projectCode,
        client: item.companyName,
        projectName: item.projectName || "",
        amount: getProjectAmountInIdr(amountItem),
        grandTotal: item.grandTotal,
        companyId: item.companyId || null,
        baseCurrencyCode: item.baseCurrencyCode,
        proposeDates: item.proposeDates || [],
      };
      }));

      // When no server-side sort is applied, default sort by createdAt descending (newest first)
      if (
        !effectiveSort?.key ||
        !PROJECT_SORT_KEY_TO_BACKEND[effectiveSort.key]
      ) {
        mappedProjects.sort(
          (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
        );
      }

      setProjects(mappedProjects);
    } catch (err) {
      showAlert("warning", "Failed to load projects data");
      setProjects([]);
      setTotalAmount(0);
    } finally {
      setLoading(false);
    }
  };

  // ✅ SEARCH: Handle search on Enter key press (reset to page 0)
  const handleSearch = () => {
    setPage(0);
    const currentTaggingIds = Array.isArray(appliedFilters.taggingIds)
      ? appliedFilters.taggingIds
      : [];
    persistState({
      filters: appliedFilters,
      search: searchQuery,
      page: 0,
      size,
    });
    fetchProjects(searchQuery, currentTaggingIds, 0, size, sortConfig);
  };

  // ✅ SEARCH: Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  // ✅ SEARCH: Handle search input change with auto-clear functionality
  const handleSearchChange = (value) => {
    setSearchQuery(value);

    // If search is cleared (empty), immediately fetch first page
    if (value === "" || value.trim() === "") {
      setPage(0);
      const currentTaggingIds = Array.isArray(appliedFilters.taggingIds)
        ? appliedFilters.taggingIds
        : [];
      fetchProjects("", currentTaggingIds, 0, size, sortConfig);
    }
  };

  // Calculate remaining years (same logic as DetailProject): from project_sections or projectServices
  const calculateRemainingYears = (projectSectionsOrProjectServices) => {
    if (
      !projectSectionsOrProjectServices ||
      !Array.isArray(projectSectionsOrProjectServices)
    )
      return 0;
    const currentYear = new Date().getFullYear();
    let maxYear = currentYear;

    const collectMaxYearFromStandards = (standards) => {
      if (!standards || !Array.isArray(standards)) return;
      standards.forEach((s) => {
        if (
          s.selectedYear != null &&
          typeof s.selectedYear === "number" &&
          s.selectedYear > maxYear
        ) {
          maxYear = s.selectedYear;
        }
        if (s.proposeDate) {
          const proposeYear = new Date(s.proposeDate).getFullYear();
          if (!Number.isNaN(proposeYear) && proposeYear > maxYear)
            maxYear = proposeYear;
        }
        if (s.children && Array.isArray(s.children))
          collectMaxYearFromStandards(s.children);
      });
    };

    // API list returns projectServices: [{ standards: [...] }]
    const hasStandards = projectSectionsOrProjectServices.some(
      (item) => item.standards && Array.isArray(item.standards),
    );
    if (hasStandards) {
      projectSectionsOrProjectServices.forEach((ps) => {
        if (ps.standards && Array.isArray(ps.standards))
          collectMaxYearFromStandards(ps.standards);
      });
    } else {
      // Detail-style project_sections: sections with services[].selectedYear and sub_services[].selectedYear
      projectSectionsOrProjectServices.forEach((section) => {
        const services = section.services || [];
        services.forEach((service) => {
          if (
            service.selectedYear != null &&
            typeof service.selectedYear === "number" &&
            service.selectedYear > maxYear
          ) {
            maxYear = service.selectedYear;
          }
          const sub = service.sub_services || [];
          sub.forEach((subService) => {
            if (
              subService.selectedYear != null &&
              typeof subService.selectedYear === "number" &&
              subService.selectedYear > maxYear
            ) {
              maxYear = subService.selectedYear;
            }
          });
        });
      });
    }

    return Math.max(1, maxYear - currentYear + 1);
  };

  // Years left from project end date when projectServices/project_sections not available (e.g. list API)
  const yearsLeftFromEndDate = (validUntil) => {
    if (!validUntil) return 1;
    const end = new Date(validUntil);
    if (Number.isNaN(end.getTime())) return 1;
    const currentYear = new Date().getFullYear();
    const endYear = end.getFullYear();
    return Math.max(1, endYear - currentYear + 1);
  };

  // Helper: format date as dd/mm/yyyy
  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return "";
    try {
      const d = new Date(dateString);
      if (Number.isNaN(d.getTime())) return dateString;
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch (e) {
      return dateString;
    }
  };

  // Helper: format date with time as dd/mm/yyyy HH:mm
  const formatDateDisplay = (dateValue) => {
    if (!dateValue) return "";

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // ✅ PERFORMANCE: Fetch only project data on mount; filter options lazy-loaded when filter panel opens
  // Guard with ref to prevent double-fetch (capabilitiesLoading + user?.id both change on mount)
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (!capabilitiesLoading && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      const initializeData = async () => {
        try {
          const saved = loadPersistedState();
          if (saved) {
            const restoredFilters = saved.filters ?? getEmptyProjectFilters();
            const restoredSearch = saved.search ?? "";
            const restoredPage = saved.page ?? 0;
            const restoredSize = saved.size ?? 10;

            setAppliedFilters(restoredFilters);
            setFilterData(restoredFilters);
            setSearchQuery(restoredSearch);
            setPage(restoredPage);
            setSize(restoredSize);

            const taggingIds = Array.isArray(restoredFilters.taggingIds)
              ? restoredFilters.taggingIds
              : [];
            await fetchProjects(
              restoredSearch,
              taggingIds,
              restoredPage,
              restoredSize,
              sortConfig,
              restoredFilters,
            );
          } else {
            await fetchProjects(searchQuery, [], 0, size, sortConfig);
          }
        } catch (error) {
          console.error("Error during initialization:", error);
        }
      };
      initializeData();
    }
  }, [capabilitiesLoading, user?.id]);

  const handleFilterChange = (name, value) => {
    setFilterData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterReset = () => {
    const emptyFilters = getEmptyProjectFilters();
    setFilterData(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPage(0);
    persistState({ filters: emptyFilters, search: searchQuery, page: 0, size });
    fetchProjects(searchQuery, [], 0, size, sortConfig, emptyFilters);
  };

  const handleFilterApply = () => {
    const { startDate, endDate } = getActivePeriodRange(filterData);
    const applied = { ...filterData, startDate, endDate };
    setAppliedFilters(applied);
    setPage(0);
    persistState({ filters: applied, search: searchQuery, page: 0, size });
    const newTaggingIds = Array.isArray(applied.taggingIds)
      ? [...applied.taggingIds]
      : [];
    fetchProjects(searchQuery, newTaggingIds, 0, size, sortConfig, applied);
  };

  const resolveProjectOptionLabel = (options = [], value) => {
    const match = (options || []).find(
      (opt) => String(opt.value ?? opt.id) === String(value),
    );
    return match?.label ?? match?.name ?? String(value);
  };

  const withInactiveBadge = (u) =>
    u.status === "0" ? { ...u, label: `${u.label} (Inactive)` } : u;

  // Cascade: filter people options by selected department(s); mark inactive users
  const cascadedProjectAssignedUsers = useMemo(() => {
    const selectedDepts = (
      Array.isArray(filterData.department) ? filterData.department : []
    ).map(String);
    const base = (filterOptions.assignedUsers || []).map(withInactiveBadge);
    if (!selectedDepts.length) return base;
    return base.filter((u) => {
      const d = u.departmentId;
      return d != null && selectedDepts.includes(String(d));
    });
  }, [filterData.department, filterOptions.assignedUsers]);

  const cascadedProjectCreatedByUsers = useMemo(() => {
    return (filterOptions.createdByUsers || []).map(withInactiveBadge);
  }, [filterOptions.createdByUsers]);

  const projectFilterFields = useMemo(
    () => [
      {
        key: "createdDate",
        label: "Created At",
        type: "dateRange",
        fromKey: "createdDateFrom",
        toKey: "createdDateTo",
      },
      {
        key: "projectStartDate",
        label: "Project Start Date",
        type: "dateRange",
        fromKey: "projectStartDateFrom",
        toKey: "projectStartDateTo",
      },
      {
        key: "projectEndDate",
        label: "Project End Date",
        type: "dateRange",
        fromKey: "projectEndDateFrom",
        toKey: "projectEndDateTo",
      },
      {
        key: "proposeDate",
        label: "Audit Date",
        type: "dateRange",
        fromKey: "proposeDateFrom",
        toKey: "proposeDateTo",
      },
      {
        key: "department",
        label: "Department",
        type: "multiselect",
        options: filterOptions.departments,
      },
      {
        key: "assignedUser",
        label: "Assigned To",
        type: "multiselect",
        options: cascadedProjectAssignedUsers,
      },
      {
        key: "createdBy",
        label: "Created By",
        type: "multiselect",
        options: cascadedProjectCreatedByUsers,
      },
      {
        key: "branchId",
        label: "Branch",
        type: "multiselect",
        options: (branches || []).map((b) => ({
          value: String(b.id),
          label: b.name || String(b.id),
        })),
      },
      {
        key: "sector",
        label: "Sector",
        type: "multiselect",
        options: filterOptions.sectors,
      },
      {
        key: "source",
        label: "Source",
        type: "multiselect",
        options: filterOptions.sources,
      },
      {
        key: "standards",
        label: "Standard",
        type: "multiselect",
        options: filterOptions.standards,
      },
      {
        key: "service",
        label: "Service",
        type: "multiselect",
        options: filterOptions.services,
      },
      {
        key: "taggingIds",
        label: "Tagging",
        type: "multiselect",
        options: (taggingOptions || []).map((t) => ({
          value: String(t.id),
          label: t.name || String(t.id),
        })),
      },
    ],
    [
      filterOptions,
      branches,
      taggingOptions,
      cascadedProjectAssignedUsers,
      cascadedProjectCreatedByUsers,
    ],
  );

  const activeFilterCount = getActiveFilterCount(
    appliedFilters,
    projectFilterFields,
  );

  const projectFilterTags = useMemo(() => {
    const tags = [];

    if (appliedFilters.period) {
      tags.push({
        key: "period",
        label: `Period: ${String(appliedFilters.period).replace(/_/g, " ")}`,
      });
    } else if (appliedFilters.startDate || appliedFilters.endDate) {
      tags.push({
        key: "period",
        label: `Period: ${[appliedFilters.startDate, appliedFilters.endDate].filter(Boolean).join(" - ")}`,
      });
    }

    if (
      appliedFilters.projectStartDateFrom ||
      appliedFilters.projectStartDateTo
    )
      tags.push({
        key: "projectStartDate",
        label: `Project Start: ${appliedFilters.projectStartDateFrom || "..."} → ${appliedFilters.projectStartDateTo || "..."}`,
      });
    if (appliedFilters.projectEndDateFrom || appliedFilters.projectEndDateTo)
      tags.push({
        key: "projectEndDate",
        label: `Project End: ${appliedFilters.projectEndDateFrom || "..."} → ${appliedFilters.projectEndDateTo || "..."}`,
      });
    if (appliedFilters.proposeDateFrom || appliedFilters.proposeDateTo)
      tags.push({
        key: "proposeDate",
        label: `Audit Date: ${appliedFilters.proposeDateFrom || "..."} → ${appliedFilters.proposeDateTo || "..."}`,
      });

    const multiTag = (key, label, options) => {
      const value = appliedFilters[key];
      if (!Array.isArray(value) || value.length === 0) return;
      if (value.length === 1) {
        tags.push({
          key,
          label: `${label}: ${resolveProjectOptionLabel(options, value[0])}`,
        });
      } else {
        tags.push({ key, label: `${label}: ${value.length} selected` });
      }
    };

    multiTag("assignedUser", "Assigned", filterOptions.assignedUsers);
    multiTag("createdBy", "Created By", filterOptions.createdByUsers);
    multiTag("department", "Department", filterOptions.departments);
    multiTag(
      "branchId",
      "Branch",
      (branches || []).map((b) => ({
        value: String(b.id),
        label: b.name || String(b.id),
      })),
    );
    multiTag("sector", "Sector", filterOptions.sectors);
    multiTag("source", "Source", filterOptions.sources);
    multiTag("standards", "Standard", filterOptions.standards);
    multiTag("service", "Service", filterOptions.services);
    if (
      Array.isArray(appliedFilters.taggingIds) &&
      appliedFilters.taggingIds.length > 0
    )
      tags.push({
        key: "taggingIds",
        label: `Tagging: ${appliedFilters.taggingIds.length} selected`,
      });
    return tags;
  }, [appliedFilters, filterOptions, branches]);

  const handleProjectChipRemove = (key) => {
    const nextFilters = { ...appliedFilters };
    if (key === "period") {
      nextFilters.period = "";
      nextFilters.startDate = "";
      nextFilters.endDate = "";
    } else if (key === "projectStartDate") {
      nextFilters.projectStartDateFrom = "";
      nextFilters.projectStartDateTo = "";
    } else if (key === "projectEndDate") {
      nextFilters.projectEndDateFrom = "";
      nextFilters.projectEndDateTo = "";
    } else if (key === "proposeDate") {
      nextFilters.proposeDateFrom = "";
      nextFilters.proposeDateTo = "";
    } else if (
      [
        "taggingIds",
        "assignedUser",
        "createdBy",
        "department",
        "branchId",
        "sector",
        "source",
        "standards",
        "service",
      ].includes(key)
    ) {
      nextFilters[key] = [];
    } else {
      nextFilters[key] = "";
    }

    const nextTaggingIds = Array.isArray(nextFilters.taggingIds)
      ? [...nextFilters.taggingIds]
      : [];
    setFilterData((prev) => ({ ...prev, ...nextFilters }));
    setAppliedFilters(nextFilters);
    setPage(0);
    persistState({ filters: nextFilters, search: searchQuery, page: 0, size });
    fetchProjects(
      searchQuery,
      nextTaggingIds,
      0,
      size,
      sortConfig,
      nextFilters,
    );
  };

  // Stats data for overview
  const stats = {
    leads: {
      total: 56,
      new: 40,
      existing: 16,
    },
    hot: {
      total: 37,
      new: 20,
      existing: 17,
    },
    negotiation: {
      total: 12,
      new: 10,
      existing: 2,
    },
    converted: {
      total: 56,
      new: 40,
      existing: 16,
    },
  };

  const handleEditProject = (project) => {
    navigate(`/projects/detail/${project.leadId}`);
  };

  const handleDeleteProject = (project) => {
    if (!canProject("delete")) {
      showAlert("danger", "You do not have permission to delete projects.");
      return;
    }
    setItemToDelete(project);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/crm/projects/${itemToDelete.id}`);
      setProjects((prev) => prev.filter((p) => p.id !== itemToDelete.id));
      showAlert(
        "success",
        `Project ${itemToDelete.project} deleted successfully`,
      );
    } catch (err) {
      console.error("Delete error:", err);
      showAlert("danger", "Failed to delete project");
    }
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleViewProject = (project) => {
    navigate(`/projects/detail/${project.leadId}`);
  };

  const handleExportExcel = () => {
    if (!canProject("export_data")) {
      showAlert("danger", "You do not have permission to export data.");
      return;
    }

    try {
      // Prepare data for Excel export
      const excelData = projects.map((row) => {
        const sectionsOrServices = row.project_sections || row.projectServices;
        const hasServices =
          sectionsOrServices &&
          Array.isArray(sectionsOrServices) &&
          sectionsOrServices.length > 0;
        const yearsLeft = hasServices
          ? calculateRemainingYears(sectionsOrServices)
          : yearsLeftFromEndDate(row.validUntil);
        const yearLeftText = `${yearsLeft} year${yearsLeft !== 1 ? "s" : ""} left`;
        return {
          Project: row.project || "",
          Client: row.client || "",
          "Project Name": row.projectName || "",
          "Project Start Date": formatDateToDDMMYYYY(row.projectDate),
          "Project End Date": formatDateToDDMMYYYY(row.validUntil),
          "Year Left": yearLeftText,
          "Partner Type": row.partnerType || "",
          // Amount is already converted to the IDR base currency during mapping.
          Amount: formatAmount(row.amount, "IDR"),
          "Created By":
            `${row.createdBy?.firstName || ""} ${row.createdBy?.lastName || ""}`.trim() ||
            "-",
          Department: row.department?.name || "N/A",
          "Assigned To":
            `${row.assignedTo?.firstName || ""} ${row.assignedTo?.lastName || ""}`.trim() ||
            "-",
          "Created At": formatDateToDDMMYYYY(row.createdAt),
          Status: row.statusDesc || "Draft",
        };
      });

      // Create worksheet from data
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths for better readability
      const columnWidths = [
        { wch: 20 }, // Project
        { wch: 30 }, // Client
        { wch: 18 }, // Project Name
        { wch: 18 }, // Project Start Date
        { wch: 18 }, // Project End Date
        { wch: 12 }, // Year Left
        { wch: 20 }, // Partner Type
        { wch: 20 }, // Amount
        { wch: 20 }, // Created By
        { wch: 20 }, // Department
        { wch: 20 }, // Assigned To
        { wch: 15 }, // Created At
        { wch: 15 }, // Status
      ];
      worksheet["!cols"] = columnWidths;

      // Create workbook and add the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Projects");

      // Generate file name with timestamp
      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const fileName = `projects_export_${timestamp}.xlsx`;

      // Write and download the file
      XLSX.writeFile(workbook, fileName);

      showAlert("success", "Projects exported to Excel successfully!");
    } catch (e) {
      console.error("Export Excel failed:", e);
      showAlert(
        "danger",
        "Failed to export projects to Excel. Please try again.",
      );
    }
  };

  const handleExportPDF = () => {
    if (!canProject("export_data")) {
      showAlert("danger", "You do not have permission to export data.");
      return;
    }

    try {
      const win = window.open("", "_blank");
      if (!win) return;

      const style = `
        <style>
          body { font-family: Arial, sans-serif; padding: 16px; }
          h2 { margin: 0 0 12px 0; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #ccc; padding: 8px; text-align: left; font-size: 12px; }
          th { background: #f5f5f5; }
        </style>
      `;

      const header = [
        "Project",
        "Client",
        "Project Name",
        "Project Start Date",
        "Project End Date",
        "Partner Type",
        "Amount",
        "Created By",
        "Department",
        "Assigned To",
        "Created At",
        "Status",
      ];
      const rows = projects.map((r) => [
        r.project || "",
        r.client || "",
        r.projectName || "",
        formatDateToDDMMYYYY(r.projectDate),
        formatDateToDDMMYYYY(r.validUntil),
        r.partnerType || "",
        formatAmount(r.amount, "IDR"),
        `${r.createdBy?.firstName || ""} ${r.createdBy?.lastName || ""}`.trim() ||
          "-",
        r.department?.name || "N/A",
        `${r.assignedTo?.firstName || ""} ${r.assignedTo?.lastName || ""}`.trim() ||
          "-",
        formatDateToDDMMYYYY(r.createdAt),
        r.statusDesc || "Draft",
      ]);

      const table = `
        <table>
          <thead>
            <tr>${header.map((h) => `<th>${h}</th>`).join("")}</tr>
          </thead>
          <tbody>
            ${rows.map((cols) => `<tr>${cols.map((c) => `<td>${c != null ? String(c) : ""}</td>`).join("")}</tr>`).join("")}
          </tbody>
        </table>
      `;

      win.document.write(
        `<!doctype html><html><head><meta charset="utf-8">${style}<title>Projects Export</title></head><body><h2>Projects Export</h2>${table}</body></html>`,
      );
      win.document.close();
      win.focus();
      win.print();

      showAlert("success", "Projects exported to PDF successfully!");
    } catch (e) {
      console.error("Export PDF failed:", e);
      showAlert(
        "danger",
        "Failed to export projects to PDF. Please try again.",
      );
    }
  };

  const columns = [
    {
      key: "project",
      label: "Project",
      sortable: true,
      className: "text-truncate",
      render: (value, row) => (
        <a
          href={`/projects/detail/${row.leadId}`}
          onClick={(e) => {
            // Allow right-click, Ctrl+Click, Cmd+Click to open in new tab
            if (e.ctrlKey || e.metaKey || e.button === 1) {
              return; // Let browser handle it
            }
            e.preventDefault();
            navigate(`/projects/detail/${row.leadId}`);
          }}
          onAuxClick={(e) => {
            // Handle middle mouse button click
            if (e.button === 1) {
              return; // Let browser handle it
            }
          }}
          className="text-primary text-truncate d-inline-block text-decoration-none"
          style={{ cursor: "pointer", maxWidth: "150px", width: "150px" }}
          title={value || row.projectCode}
        >
          {value}
        </a>
      ),
    },
    {
      key: "client",
      label: "Client",
      sortable: true,
      className: "text-truncate",
      render: (value, item) => (
        <a
          href={`/clients/${item.companyId}`}
          className="text-primary text-truncate d-inline-block text-decoration-none"
          style={{
            cursor: "pointer",
            maxWidth: "200px",
            width: "200px",
          }}
          onClick={(e) => {
            // Allow right-click, Ctrl+Click, Cmd+Click to open in new tab
            if (e.ctrlKey || e.metaKey || e.button === 1) {
              return; // Let browser handle it
            }
            e.preventDefault();
            navigate(`/clients/${item.companyId}`);
          }}
          onAuxClick={(e) => {
            // Handle middle mouse button click
            if (e.button === 1) {
              return; // Let browser handle it
            }
          }}
          title={value}
          aria-label={`View client details for ${value}`}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate(`/clients/${item.companyId}`);
            }
          }}
        >
          {value}
        </a>
      ),
    },
    {
      key: "yearLeft",
      label: "Year Left",
      sortable: true,
      className: "text-truncate",
      render: (value, item) => {
        const sectionsOrServices =
          item.project_sections || item.projectServices;
        const hasServices =
          sectionsOrServices &&
          Array.isArray(sectionsOrServices) &&
          sectionsOrServices.length > 0;
        const years = hasServices
          ? calculateRemainingYears(sectionsOrServices)
          : yearsLeftFromEndDate(item.validUntil);
        const text = `${years} year${years !== 1 ? "s" : ""} left`;
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: "100px", width: "100px" }}
            title={text}
          >
            {text}
          </span>
        );
      },
    },
    {
      key: "tagging",
      label: "Tagging",
      sortable: true,
      className: "text-truncate",
      render: (value, item) => {
        const raw = item.tagging ?? value ?? "";
        const rawStr = typeof raw === "string" ? raw : "";
        const tags = rawStr
          ? rawStr
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : [];
        if (tags.length === 0) return <span className="text-muted">-</span>;
        return (
          <span
            className="d-inline-flex flex-wrap gap-1"
            style={{ maxWidth: "180px" }}
          >
            {tags.map((tag, i) => (
              <span
                key={i}
                className="badge bg-blue-lt text-wrap"
                title={tag}
                style={{ fontSize: "0.7rem" }}
              >
                {tag}
              </span>
            ))}
          </span>
        );
      },
    },
    {
      key: "projectName",
      label: "Project Name",
      sortable: true,
      className: "text-truncate",
      render: (value, row) => (
        <a
          href={`/leads/detail/${row.leadId}`}
          className="text-primary text-truncate d-inline-block text-decoration-none"
          style={{ cursor: "pointer", maxWidth: "250px", width: "250px" }}
          onClick={(e) => {
            // Allow right-click, Ctrl+Click, Cmd+Click to open in new tab
            if (e.ctrlKey || e.metaKey || e.button === 1) {
              return; // Let browser handle it
            }
            e.preventDefault();
            navigate(`/leads/detail/${row.leadId}`);
          }}
          onAuxClick={(e) => {
            // Handle middle mouse button click
            if (e.button === 1) {
              return; // Let browser handle it
            }
          }}
          title={`View lead details for ${value}`}
          aria-label={`View lead details for ${value}`}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate(`/leads/detail/${row.leadId}`);
            }
          }}
        >
          {value}
        </a>
      ),
    },
    {
      key: "service",
      label: "Service",
      sortable: false,
      className: "text-truncate",
      render: (value, item) => {
        const services = (item.serviceNames || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        if (services.length === 0) return <span className="text-muted">-</span>;
        return (
          <span
            className="d-inline-flex flex-wrap gap-1"
            style={{ maxWidth: "180px" }}
          >
            {services.map((svc, i) => (
              <span
                key={i}
                className="badge bg-green-lt text-wrap"
                title={svc}
                style={{ fontSize: "0.7rem" }}
              >
                {svc}
              </span>
            ))}
          </span>
        );
      },
    },
    {
      key: "standard",
      label: "Standard",
      sortable: false,
      className: "text-truncate",
      render: (value, item) => {
        const standards = (item.standardNames || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        if (standards.length === 0)
          return <span className="text-muted">-</span>;
        return (
          <span
            className="d-inline-flex flex-wrap gap-1"
            style={{ maxWidth: "180px" }}
          >
            {standards.map((std, i) => (
              <span
                key={i}
                className="badge bg-azure-lt text-wrap"
                title={std}
                style={{ fontSize: "0.7rem" }}
              >
                {std}
              </span>
            ))}
          </span>
        );
      },
    },
    {
      key: "branch",
      label: "Branch",
      sortable: false,
      className: "text-truncate",
      render: (value, item) => {
        const branchName = item.branchName || "N/A";
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: "150px", width: "150px" }}
            title={branchName}
          >
            {branchName}
          </span>
        );
      },
    },
    {
      key: "department",
      label: "Department",
      sortable: true,
      className: "text-truncate",
      render: (value, item) => {
        const deptValue =
          item?.departmentName || item?.department?.name || "N/A";
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: "150px", width: "150px" }}
            title={deptValue}
          >
            {deptValue}
          </span>
        );
      },
    },
    {
      key: "assignedTo",
      label: "Assigned To",
      sortable: true,
      className: "text-truncate",
      render: (value, item) => {
        const assignedValue =
          item?.assignedToUser?.fullName ||
          (item?.assignedToUser?.firstName && item?.assignedToUser?.lastName)
            ? `${item.assignedToUser.firstName} ${item.assignedToUser.lastName}`.trim()
            : item?.assignedTo || "N/A";
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: "150px", width: "150px" }}
            title={assignedValue}
          >
            {assignedValue}
          </span>
        );
      },
    },
    {
      key: "projectDate",
      label: "Project Start Date",
      sortable: true,
      className: "text-truncate",
      render: (value) => {
        const dateValue = formatDateDisplay(value);
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: "140px", width: "140px" }}
            title={dateValue}
          >
            {dateValue}
          </span>
        );
      },
    },
    {
      key: "validUntil",
      label: "Project End Date",
      sortable: true,
      className: "text-truncate",
      render: (value) => {
        const dateValue = formatDateDisplay(value);
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: "140px", width: "140px" }}
            title={dateValue}
          >
            {dateValue}
          </span>
        );
      },
    },
    {
      key: "partnerType",
      label: "Partner Type",
      sortable: true,
      className: "text-truncate",
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: "180px", width: "180px" }}
          title={value}
        >
          {value}
        </span>
      ),
    },
    {
      key: "amount",
      label: "Amount",
      sortable: true,
      className: "text-truncate",
      render: (value, item) => {
        // `value` is IDR-converted, matching the Proposals list.
        const formattedValue = formatAmount(value, "IDR");
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: "150px", width: "150px" }}
            title={formattedValue}
          >
            {formattedValue}
          </span>
        );
      },
    },
    {
      key: "auditDates",
      label: "Audit Date",
      sortable: false,
      className: "text-truncate",
      render: (value, item) => {
        const dates = item.proposeDates || [];
        if (dates.length === 0) return <span className="text-muted">-</span>;
        const formatted = dates
          .map((d) => {
            const parts = d.split("-");
            if (parts.length === 3)
              return `${parts[2]}/${parts[1]}/${parts[0]}`;
            return d;
          })
          .join(", ");
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: "180px", width: "180px" }}
            title={formatted}
          >
            {formatted}
          </span>
        );
      },
    },
    {
      key: "createdAt",
      label: "Created Date",
      sortable: true,
      className: "text-truncate",
      render: (value) => {
        const dateValue = formatDateDisplay(value);
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: "140px", width: "140px" }}
            title={dateValue}
          >
            {dateValue}
          </span>
        );
      },
    },
  ];

  return (
    <div className="container-xl">
      {/* Alert Component */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      {/* Confirmation Modal for Delete */}
      {showDeleteModal && (
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setItemToDelete(null);
          }}
          onConfirm={confirmDelete}
          title="Delete Item"
          message={`Are you sure you want to delete this? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmVariant="danger"
        />
      )}

      <div className="row g-2 align-items-center">
        <div className="col">
          {/* <h3 className="page-subtitle m-0">Overview</h3> */}
          <h2 className="page-title">Dashboard Project</h2>
        </div>
      </div>
      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul
                  className="nav nav-tabs card-header-tabs"
                  data-bs-toggle="tabs"
                  role="tablist"
                >
                  {/* <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-overview" className="nav-link active" data-bs-toggle="tab" aria-selected="true" role="tab">Overview</a>
                  </li> */}
                  <li className="nav-item-tab" role="presentation">
                    <a
                      href="#tabs-list-data"
                      className="nav-link active"
                      data-bs-toggle="tab"
                      aria-selected="false"
                      tabIndex="-1"
                      role="tab"
                    >
                      List Data
                    </a>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  {/* <div className="tab-pane active show" id="tabs-overview" role="tabpanel">
                    <OverviewView stats={stats} />
                  </div> */}
                  <div
                    className="tab-pane active show"
                    id="tabs-list-data"
                    role="tabpanel"
                  >
                    <GenericView
                      data={projects}
                      columns={columns}
                      onEdit={handleEditProject}
                      onDelete={
                        canProject("delete") ? handleDeleteProject : undefined
                      }
                      onView={handleViewProject}
                      filterComponent={
                        <FilterButton
                          offcanvasId="offcanvasProject"
                          activeCount={activeFilterCount}
                          onClick={handleOpenFilter}
                        />
                      }
                      searchComponent={
                        <SearchInput
                          value={searchQuery}
                          onChange={handleSearchChange}
                          onKeyPress={handleSearchKeyPress}
                          placeholder="Search project"
                        />
                      }
                      actionButtons={
                        <ActionButtons
                          onExportExcel={
                            canProject("export_data")
                              ? handleExportExcel
                              : undefined
                          }
                          onExportPDF={
                            canProject("export_data")
                              ? handleExportPDF
                              : undefined
                          }
                        />
                      }
                      belowTopControlsContent={
                        <FilterAwarenessBar
                          title="Projects is showing a filtered view"
                          summary={`Filtered by ${activeFilterCount} ${activeFilterCount === 1 ? "criterion" : "criteria"}`}
                          detail={projectFilterTags
                            .map((tag) => tag.label)
                            .join(" · ")}
                          loading={loading || capabilitiesLoading}
                          activeCount={activeFilterCount}
                          filterTags={projectFilterTags}
                          onRemoveFilter={handleProjectChipRemove}
                          onResetFilters={handleFilterReset}
                        />
                      }
                      paginationTopContent={
                        <div className="row mb-3">
                          <div className="col-md-3">
                            <div className="d-flex flex-column">
                              <strong>Total records for this page:</strong>
                              <span>{projects.length}</span>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="d-flex flex-column">
                              <strong>Total Amount (This Page):</strong>
                              <span>
                                {formatAmount(
                                  projects.reduce(
                                    (sum, p) =>
                                      sum +
                                      projectBaseAmount(p),
                                    0,
                                  ),
                                  "IDR",
                                )}
                              </span>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="d-flex flex-column">
                              <strong>Total Records:</strong>
                              <span>{totalElements}</span>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="d-flex flex-column">
                              <strong>Total Amount:</strong>
                              <span>{formatAmount(totalAmount, "IDR")}</span>
                            </div>
                          </div>
                        </div>
                      }
                      checkboxSelection={true}
                      onRowClick={handleViewProject}
                      emptyStateMessage="No projects found"
                      isLoading={loading || capabilitiesLoading}
                      pagination={{
                        currentPage: page + 1,
                        totalRows: totalElements,
                        rowsPerPage: size,
                        onPageChange: (newPage) => {
                          const nextPage = newPage - 1;
                          setPage(nextPage);
                          persistState({
                            filters: appliedFilters,
                            search: searchQuery,
                            page: nextPage,
                            size,
                          });
                          fetchProjects(
                            searchQuery,
                            appliedFilters.taggingIds || [],
                            nextPage,
                            size,
                            sortConfig,
                          );
                        },
                        onRowsPerPageChange: (newSize) => {
                          setSize(newSize);
                          setPage(0);
                          persistState({
                            filters: appliedFilters,
                            search: searchQuery,
                            page: 0,
                            size: newSize,
                          });
                          fetchProjects(
                            searchQuery,
                            appliedFilters.taggingIds || [],
                            0,
                            newSize,
                            sortConfig,
                          );
                        },
                      }}
                      onSortChange={(newSortConfig) => {
                        setSortConfig(newSortConfig);
                        setPage(0);
                        fetchProjects(
                          searchQuery,
                          appliedFilters.taggingIds || [],
                          0,
                          size,
                          newSortConfig,
                        );
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Filter Offcanvas */}
      <AdvancedFilterPanel
        offcanvasId="offcanvasProject"
        title="Advanced Filter"
        filterData={filterData}
        onChange={handleFilterChange}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
        filterFields={projectFilterFields}
      />
    </div>
  );
};

export default Project;
